import React from 'react';
import {
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Box
} from '@mui/material';
import { List } from '@phosphor-icons/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { DrawerHeader } from '../DrawerHeader/DrawerHeader';
import { NavigationItems } from '../NavigationItems/NavigationItems';
import { ResizeHandle } from '../ResizeHandle/ResizeHandle';
import { ProfileMenu } from '../ProfileMenu/ProfileMenu';
import { SettingsModal } from '../SettingsModal/SettingsModal';
import { useLeftMenuState } from '@/hooks/useLeftMenuState';
import { useDrawerResize } from '@/hooks/useDrawerResize';
import { NAVIGATION_ITEMS, DRAWER_CONFIG, isIPad } from '@/constants';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useHelpDrawer } from '@/contexts/HelpDrawerContext';

export const IntakeLeftMenu: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = isIPad();
  const { user, logout } = useAuth0();
  const location = useLocation();
  const { appUser } = useAppUser();
  const isHelpPage = location.pathname === '/help';
  
  // Use help drawer context (always available via provider)
  const { openDrawer: openHelpDrawer } = useHelpDrawer();

  // No global offset; DrawerHeader itself matches AppBar height

  // Use custom hooks for state management and resize functionality
  const {
    isOpen,
    isCollapsed,
    settingsModalOpen,
    profileAnchorEl,
    setIsOpen,
    toggleDrawer,
    toggleCollapse,
    handleProfileClick,
    handleProfileClose,
    handleSettingsOpen,
    handleSettingsClose
  } = useLeftMenuState(isMobile);

  // Keep original resize hook
  const { drawerRef, drawerWidth, isResizingLeft, handleLeftMouseDown } =
    useDrawerResize();

  // Determine user initials for the avatar
  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  // Common content for both mobile drawer and desktop box
  const menuContent = (
    <>
      <DrawerHeader
        user={user}
        initials={initials ?? ''}
        isMobile={isMobile}
        onProfileClick={handleProfileClick}
        isCollapsed={!isMobile && isCollapsed}
        onToggleCollapse={!isMobile ? toggleCollapse : undefined}
      />
      {/* Only show Search, Tasks, People, and Admin in the nav for super admins (Ask is now available to all) */}
      <NavigationItems
        navItems={
          appUser?.isSuperAdmin
            ? NAVIGATION_ITEMS
            : NAVIGATION_ITEMS.filter(
                (item) =>
                  item.path !== '/search' &&
                  item.path !== '/tasks' &&
                  item.path !== '/people' &&
                  item.path !== '/admin'
              )
        }
        location={location}
        isCollapsed={!isMobile && isCollapsed}
        onNavigate={isMobile ? () => setIsOpen(false) : undefined}
      />

      {/* Resize handle - only show in desktop mode and when expanded */}
      {!isMobile && !isCollapsed && (
        <ResizeHandle
          isResizing={isResizingLeft}
          onMouseDown={handleLeftMouseDown}
        />
      )}
    </>
  );

  // Direct DOM manipulation via effect to fix the increasing resize issue
  React.useEffect(() => {
    if (!drawerRef.current || isMobile) return;
    if (isResizingLeft) {
      drawerRef.current.style.width = `${drawerWidth}px`;
    } else {
      // Let sx-driven width control when not actively resizing
      drawerRef.current.style.width = '';
    }
  }, [drawerWidth, isResizingLeft, isMobile]);

  // Attio-style clean menu styling
  const menuStyling = {
    backgroundColor: theme.palette.background.paper,
    overflowX: 'hidden',
    borderRight: `1px solid ${theme.palette.divider}`
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Menu toggle button */}
      {!isOpen && isMobile && (
        <IconButton
          onClick={() => {
            if (isHelpPage && openHelpDrawer) {
              openHelpDrawer();
            } else {
              toggleDrawer();
            }
          }}
          className="mobile-touch-target"
          sx={{
            position: 'fixed',
            top: 10, // Adjusted to center 36px button in header
            left: 16,
            zIndex: 1301,
            width: 36,
            height: 36,
            borderRadius: '6px',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: '#d1d5db'
            }
          }}
        >
          <List size={18} weight="regular" />
        </IconButton>
      )}

      {/* Conditional rendering based on device type */}
      {isMobile ? (
        /* Mobile Drawer */
        isOpen ? (
          <Drawer
            key="mobile-drawer" // Force remount when switching between desktop/mobile
            variant="temporary"
            anchor="left"
            open
            onClose={() => setIsOpen(false)}
            keepMounted={false} // Don't keep mounted to prevent backdrop issues on viewport changes
            ModalProps={{
              keepMounted: false,
              disableScrollLock: true,
              disableEnforceFocus: true,
              disableRestoreFocus: true,
              sx: {
                '& .MuiBackdrop-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)' // More opaque backdrop
                }
              }
            }}
            slotProps={{
              paper: {
                'data-onboarding': 'sidebar',
                sx: {
                  width: isTablet ? DRAWER_CONFIG.IPAD_WIDTH : drawerWidth,
                  backgroundColor: menuStyling.backgroundColor,
                  overflowX: menuStyling.overflowX
                }
              } as any
            }}
          >
            {menuContent}
          </Drawer>
        ) : null
      ) : (
        /* Desktop Box instead of Drawer */
        <Box
          key="desktop-menu-box" // Force remount when switching between desktop/mobile
          ref={drawerRef}
          id="desktop-menu-box"
          data-onboarding="sidebar"
          sx={{
            position: 'relative',
            left: 0,
            height: '100vh',
            width: isOpen
              ? isCollapsed
                ? DRAWER_CONFIG.COLLAPSED_WIDTH
                : drawerWidth
              : 0,
            transition: isResizingLeft
              ? 'none'
              : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxSizing: 'border-box',
            margin: 0,
            overflowY: 'auto',
            // borderRight is provided by menuStyling; avoid duplicate assignment
            boxShadow: 'none',
            zIndex: 1200,
            ...menuStyling,
            ...(isCollapsed
              ? {
                  // Remove hover-grow effect when collapsed
                  '&:hover': {
                    width: DRAWER_CONFIG.COLLAPSED_WIDTH
                  }
                }
              : {})
          }}
        >
          {isOpen && menuContent}
        </Box>
      )}

      {/* Profile menu popup */}
      <ProfileMenu
        anchorEl={profileAnchorEl}
        onClose={handleProfileClose}
        onSettingsOpen={handleSettingsOpen}
        onLogout={() =>
          logout({
            logoutParams: {
              returnTo: window.location.origin
            }
          })
        }
        onDrawerClose={isMobile ? () => setIsOpen(false) : undefined}
      />

      {/* Settings modal */}
      <SettingsModal open={settingsModalOpen} onClose={handleSettingsClose} />
    </Box>
  );
};
